import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/shadcn/card";
import {
  Clock,
  DollarSign,
  CheckCircle2,
  XCircle,
  AlertCircle,
  AlarmClock,
  ShoppingCart,
} from "lucide-react";
import { quotesAPI, tradesAPI } from "@/services/api";
import { QuoteStatus } from "@/types/quotes";

export function StatsCards() {
  const [stats, setStats] = useState({
    // totalVolume: 0,
    activeQuotes: 0,
    acceptedQuotes: 0,
    expiredQuotes: 0,
    acceptanceRate: 0,
    expiryRate: 0,
    avgResponseTime: 0,
    completedTrades: 0,
  });

  useEffect(() => {
    async function fetchStats() {
      try {
        // Fetch all quotes with a large page size
        // Get all quotes with Active, Accepted and Expired statuses
        const allQuotesResponse = await quotesAPI.getQuotes({ size: 1000, status: [QuoteStatus.ACTIVE, QuoteStatus.ACCEPTED, QuoteStatus.EXPIRED] });
        const allQuotes = allQuotesResponse.items;

        // Fetch all accepted quotes with a large page size
        const acceptedQuotesResponse = await quotesAPI.getAcceptedQuotes({ size: 1000 });
        const acceptedQuotes = acceptedQuotesResponse.items;

        // Fetch all trades with a large page size
        const tradesResponse = await tradesAPI.getTrades({ size: 1000 });
        const trades = tradesResponse?.items || [];

        // // Calculate total volume in USD
        // const totalVolume = trades.reduce((acc, trade) => {
        //   const amount = parseFloat(trade.total_to);
        //   return acc + amount;
        // }, 0);

        const activeQuotes = allQuotes.filter(
          (quote) => quote.status === QuoteStatus.ACTIVE
        ).length;
        const expiredQuotes = allQuotes.filter(
          (quote) => quote.status === QuoteStatus.EXPIRED
        ).length;
        const acceptedQuotesCount = acceptedQuotes.length;
        const acceptanceRate =
          allQuotes.length > 0
            ? (acceptedQuotesCount / allQuotes.length) * 100
            : 0;

        // Calculate average response time in minutes
        const avgResponseTime = acceptedQuotes.reduce((acc, quote) => {
          const acceptedAt = new Date(quote.accepted_at).getTime();
          return acc + acceptedAt;
        }, 0) / (acceptedQuotes.length * 60000); // Convert to minutes

        const expiryRate = (expiredQuotes / allQuotes.length) * 100;

        setStats({
        //   totalVolume,
          activeQuotes,
          acceptedQuotes: acceptedQuotesCount,
          expiredQuotes,
          acceptanceRate,
          expiryRate,
          avgResponseTime,
          completedTrades: trades.length,
        });
      } catch (error) {
        console.error("Failed to fetch stats:", error);
      }
    }
    fetchStats();
  }, []);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {/* <Card>
        <CardContent className="pt-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Total Volume
              </p>
              <h3 className="text-2xl font-bold mt-2">
                {`${formatPrice(stats.totalVolume.toString())} USD`}
              </h3>
            </div>
            <div className="bg-green-100 p-2 rounded-lg">
              <DollarSign className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </CardContent>
      </Card> */}

      <Card>
        <CardContent className="pt-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Active Quotes
              </p>
              <h3 className="text-2xl font-bold mt-2">{stats.activeQuotes}</h3>
            </div>
            <div className="bg-blue-100 p-2 rounded-lg">
              <AlertCircle className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Accepted Quotes
              </p>
              <h3 className="text-2xl font-bold mt-2">
                {stats.acceptedQuotes}
              </h3>
            </div>
            <div className="bg-green-100 p-2 rounded-lg">
              <CheckCircle2 className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Expired Quotes
              </p>
              <h3 className="text-2xl font-bold mt-2">{stats.expiredQuotes}</h3>
            </div>
            <div className="bg-purple-100 p-2 rounded-lg">
              <AlarmClock className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Quote Acceptance Rate
              </p>
              <h3 className="text-2xl font-bold mt-2">
                {stats.acceptanceRate.toFixed(2)}%
              </h3>
            </div>
            <div className="bg-purple-100 p-2 rounded-lg">
              <CheckCircle2 className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Quote Expiry Rate
              </p>
              <h3 className="text-2xl font-bold mt-2">
                {stats.expiryRate.toFixed(2)}%
              </h3>
            </div>
            <div className="bg-orange-100 p-2 rounded-lg">
              <XCircle className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Avg. Response Time
              </p>
              <h3 className="text-2xl font-bold mt-2">
                N/A
              </h3>
            </div>
            <div className="bg-yellow-100 p-2 rounded-lg">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Completed Trades
              </p>
              <h3 className="text-2xl font-bold mt-2">
                {stats.completedTrades}
              </h3>
            </div>
            <div className="bg-indigo-100 p-2 rounded-lg">
              <ShoppingCart className="h-6 w-6 text-indigo-600" />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
